<script setup lang="ts">
import '@typeform/embed/build/css/popup.css';
import { useWebsiteJsonLd } from '~/composables/jsonLd/useWebsiteJsonLd';
import { nuxtRegionService } from '~~/adapters/services/nuxt/nuxtRegionService';
import { appName, siteName } from '~~/app/constants';
import { REGION_SERVICE } from '~~/utils/symbols';
import { CookiesWarning, FixedFooterBanner } from './components/CookiesBanner';
import Toaster from './components/ui/toast/Toaster.vue';
import { useEvents } from './composables/events/useEvents';
import { isInstagramBrowserAgent } from '@/utils';
provide(REGION_SERVICE, nuxtRegionService);

const { loggedIn: isUserLoggedIn } = useUserSession();
const router = useRouter();

watchEffect(async () => {
	const isProtectedRoute = router.currentRoute.value.meta.middleware === 'auth';

	if (isProtectedRoute && !isUserLoggedIn.value) await router.push('/');
});

useWebsiteJsonLd();
useHead({
	title: appName,
	htmlAttrs: { lang: 'pt-BR' },
	link: [
		{
			rel: 'preconnect',
			href: 'https://www.google.com'
		},
		{
			rel: 'preconnect',
			href: 'https://maps.googleapis.com'
		}
	]
});
useSeoMeta({ ogSiteName: siteName });

const { initialize } = useHotjar();

const { cookiesAccepted } = useAllowCookies();

const { trackCookiesAccepted } = useEvents();
useEventBus('cookies-accepted').on(trackCookiesAccepted);

const isNotTermsPage = computed(
	() => !router.currentRoute.value.path.startsWith('/termos')
);

const shouldShowBanner = ref(false);
const isInstagramBrowser = ref(false);

function onConsent() {
	initialize();
}

const isCookiesNotAccepted = computed(
	() => !cookiesAccepted.value || cookiesAccepted.value === 'not accepted'
);

onMounted(() => {
	onConsent();

	isInstagramBrowser.value = isInstagramBrowserAgent();

	nextTick(
		() =>
			(shouldShowBanner.value =
				isNotTermsPage.value && isCookiesNotAccepted.value)
	);
});

watch(cookiesAccepted, () => {
	shouldShowBanner.value = isNotTermsPage.value && isCookiesNotAccepted.value;

	if (isInstagramBrowser.value && cookiesAccepted.value === 'accepted') {
		shouldShowBanner.value = false;
	}
});
</script>

<template>
	<VitePwaManifest />
	<NuxtLayout>
		<Toaster />
		<NuxtPage />
		<Transition
			enter-active-class="transition-all duration-300 ease-out"
			enter-from-class="translate-y-full opacity-0"
			enter-to-class="translate-y-0 opacity-100"
			leave-active-class="transition-all duration-300 ease-in"
			leave-from-class="translate-y-0 opacity-100"
			leave-to-class="translate-y-full opacity-0"
		>
			<div
				v-if="shouldShowBanner"
				class="pointer-events-auto fixed inset-x-0 bottom-0 z-[200] w-full"
			>
				<CookiesWarning />
			</div>
		</Transition>
	</NuxtLayout>
</template>

<style>
html,
body,
#__nuxt {
	height: 100dvh;
	margin: 0;
	padding: 0;
}

html.dark {
	background: #222;
	color: white;
}
</style>
